import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { InterestsPicker } from "@/components/onboarding/InterestsPicker";
import { PrivacyPromise } from "@/components/privacy/PrivacyPromise";
import { initials } from "@/lib/format";
import {
  Loader2, Upload, ArrowRight, ArrowLeft, Sparkles, HeartHandshake,
  CalendarCheck, QrCode, LayoutDashboard, Award, PartyPopper,
} from "lucide-react";

const STEPS = [
  { key: "welcome", title: "Welcome to Friday Conversations", subtitle: "Let’s set up your member card — it takes about a minute." },
  { key: "photo", title: "Add a photo", subtitle: "A friendly face helps people remember you." },
  { key: "about", title: "About you", subtitle: "A line people see first, plus a short bio." },
  { key: "work", title: "Your work", subtitle: "Where you work and how people can reach you." },
  { key: "interests", title: "Your interests", subtitle: "Pick a few topics and tell us what you’re looking for." },
  { key: "done", title: "You’re all set!", subtitle: "" },
];

export default function Onboarding() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  // Capture whether the user was ALREADY onboarded when they arrived, so that
  // completing the wizard in-session doesn't redirect away before the Done step.
  const [enteredCompleted] = useState(() => Boolean(user?.onboarding_completed));
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    avatar: "", headline: "", bio: "", title: "", company: "",
    website: "", linkedin: "", interests: [], seeking: "",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        avatar: user.avatar || "",
        headline: user.headline || "",
        bio: user.bio || "",
        title: user.title || "",
        company: user.company || "",
        website: user.website || "",
        linkedin: user.linkedin || "",
        interests: user.interests || [],
        seeking: user.seeking || "",
      }));
    }
  }, [user]);

  if (loading) return null;
  if (enteredCompleted) return <Navigate to="/portal" replace />;

  const total = STEPS.length;
  const progress = Math.round((step / (total - 1)) * 100);
  const current = STEPS[step];

  const persist = async (fields) => {
    setSaving(true);
    try {
      const res = await api.put("/users/me", fields);
      setUser(res.data);
      return true;
    } catch (e) {
      toast.error("Couldn’t save — please try again");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Please choose an image under 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "avatars");
      const res = await api.post("/storage/upload", fd);
      setForm((f) => ({ ...f, avatar: res.data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error("Upload failed — please try again");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const next = async () => {
    let ok = true;
    if (current.key === "photo") ok = await persist({ avatar: form.avatar });
    else if (current.key === "about") ok = await persist({ headline: form.headline, bio: form.bio });
    else if (current.key === "work") ok = await persist({ title: form.title, company: form.company, website: form.website, linkedin: form.linkedin });
    else if (current.key === "interests") ok = await persist({ interests: form.interests, seeking: form.seeking, onboarding_completed: true });
    if (ok) setStep((s) => Math.min(s + 1, total - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const skip = async () => {
    if (await persist({ onboarding_completed: true })) navigate("/portal");
  };

  const isDone = current.key === "done";
  const isLastInput = current.key === "interests";

  return (
    <div className="fc-hero-bg fc-paper-noise flex min-h-screen flex-col items-center px-4 py-8 sm:py-12">
      <div className="mb-6"><Wordmark /></div>

      <Card className="w-full max-w-xl border-border bg-card shadow-[var(--fc-card-shadow)]" data-testid="onboarding-wizard">
        <CardContent className="p-6 sm:p-8">
          {!isDone && (
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid="onboarding-step-indicator">Step {step + 1} of {total - 1}</span>
                <button onClick={skip} className="font-medium text-muted-foreground hover:text-foreground" data-testid="onboarding-skip">Skip for now</button>
              </div>
              <Progress value={progress} className="mt-2 h-1.5" data-testid="onboarding-progress" />
            </div>
          )}

          <div key={step} className="animate-in fade-in-50 duration-200">
            <h1 className="font-serif text-2xl font-semibold text-foreground" data-testid="onboarding-title">{current.title}</h1>
            {current.subtitle && <p className="mt-1 text-muted-foreground">{current.subtitle}</p>}

            <div className="mt-6">
              {/* Step: Welcome */}
              {current.key === "welcome" && (
                <div className="space-y-3" data-testid="onboarding-welcome">
                  {[
                    { icon: HeartHandshake, text: "Real conversations with Spokane business owners & creators." },
                    { icon: CalendarCheck, text: "Weekly themed lunches, plus a live podcast hour." },
                    { icon: QrCode, text: "Your own digital member card — scan to connect instantly." },
                  ].map((b, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 p-3">
                      <b.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <p className="text-sm text-foreground">{b.text}</p>
                    </div>
                  ))}
                  <PrivacyPromise className="mt-1" />
                </div>
              )}

              {/* Step: Photo */}
              {current.key === "photo" && (
                <div className="flex flex-col items-center gap-4 py-2">
                  <Avatar className="h-28 w-28 ring-2 ring-[hsl(var(--brand-copper)/0.4)]">
                    <AvatarImage key={form.avatar || "none"} src={form.avatar || undefined} data-testid="onboarding-avatar-image" />
                    <AvatarFallback className="bg-primary text-3xl text-primary-foreground">{initials(form.name || user?.name)}</AvatarFallback>
                  </Avatar>
                  <input id="ob-avatar" type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="onboarding-avatar-input" />
                  <Button asChild variant="secondary" disabled={uploading}>
                    <label htmlFor="ob-avatar" className="cursor-pointer">
                      {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                      {uploading ? "Uploading…" : (form.avatar ? "Change photo" : "Upload photo")}
                    </label>
                  </Button>
                  <p className="text-xs text-muted-foreground">PNG or JPG, up to 10MB — or skip and add it later.</p>
                </div>
              )}

              {/* Step: About */}
              {current.key === "about" && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="ob-headline">Ask me about <span className="text-muted-foreground">(a short line)</span></Label>
                    <Input id="ob-headline" className="mt-1.5" value={form.headline} maxLength={70}
                      onChange={(e) => setForm({ ...form, headline: e.target.value })}
                      placeholder="e.g. referrals, web design, scaling a team…" data-testid="onboarding-headline-input" />
                  </div>
                  <div>
                    <Label htmlFor="ob-bio">Short bio</Label>
                    <Textarea id="ob-bio" className="mt-1.5" rows={4} value={form.bio}
                      onChange={(e) => setForm({ ...form, bio: e.target.value })}
                      placeholder="Tell the network a little about yourself…" data-testid="onboarding-bio-input" />
                  </div>
                </div>
              )}

              {/* Step: Work */}
              {current.key === "work" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label htmlFor="ob-title">Job title</Label><Input id="ob-title" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Founder, Realtor…" data-testid="onboarding-title-input" /></div>
                  <div><Label htmlFor="ob-company">Company</Label><Input id="ob-company" className="mt-1.5" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Your business" data-testid="onboarding-company-input" /></div>
                  <div className="sm:col-span-2"><Label htmlFor="ob-website">Website</Label><Input id="ob-website" className="mt-1.5" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="onboarding-website-input" /></div>
                  <div className="sm:col-span-2"><Label htmlFor="ob-linkedin">LinkedIn</Label><Input id="ob-linkedin" className="mt-1.5" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" data-testid="onboarding-linkedin-input" /></div>
                </div>
              )}

              {/* Step: Interests */}
              {current.key === "interests" && (
                <div className="space-y-5">
                  <div>
                    <Label className="mb-2 block">Interests</Label>
                    <InterestsPicker value={form.interests} onChange={(v) => setForm({ ...form, interests: v })} />
                  </div>
                  <div>
                    <Label htmlFor="ob-seeking">What are you looking for?</Label>
                    <Textarea id="ob-seeking" className="mt-1.5" rows={2} value={form.seeking}
                      onChange={(e) => setForm({ ...form, seeking: e.target.value })}
                      placeholder="e.g. referral partners, a great accountant, podcast guests…" data-testid="onboarding-seeking-input" />
                  </div>
                </div>
              )}

              {/* Step: Done */}
              {current.key === "done" && (
                <div className="flex flex-col items-center py-2 text-center" data-testid="onboarding-done">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <PartyPopper className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="mt-4 font-serif text-xl font-semibold text-foreground">Your card is ready, {(user?.name || "friend").split(" ")[0]}!</h2>
                  <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                    Show your QR at events so people can scan and save you in seconds.
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm text-foreground" data-testid="onboarding-points">
                    <Award className="h-4 w-4 text-primary" /> You earned points for setting up your profile
                  </div>
                  <div className="mt-6 flex w-full flex-col gap-2 sm:flex-row">
                    <Button onClick={() => navigate("/portal/account/card")} className="flex-1 bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="onboarding-view-card">
                      <QrCode className="mr-2 h-4 w-4" /> View my card
                    </Button>
                    <Button onClick={() => navigate("/portal")} variant="secondary" className="flex-1" data-testid="onboarding-go-dashboard">
                      <LayoutDashboard className="mr-2 h-4 w-4" /> Go to dashboard
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer nav */}
          {!isDone && (
            <div className="mt-8 flex items-center justify-between gap-3">
              {step > 0 ? (
                <Button variant="ghost" onClick={back} disabled={saving} data-testid="onboarding-back">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
                </Button>
              ) : <span />}
              <Button onClick={next} disabled={saving || uploading} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="onboarding-next">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>
                    {current.key === "welcome" ? "Let’s go" : isLastInput ? "Finish" : "Continue"}
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
